import { apiClient } from '@/core/api/apiClient'
import { getDriverLicenseUploadContentType } from '@/components/DriversLicensePreview'
import { fileToBase64 } from '@/core/utils/fileUtils'
import { contactsQueryKey } from 'hooks/contacts/useContacts'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

const extractUploadResult = (result: unknown): { storagePath?: string; url?: string } => {
  const data = (result as { data?: { storagePath?: string; url?: string } })?.data ?? result
  return data as { storagePath?: string; url?: string }
}

const extractUrl = (result: unknown): string | undefined => {
  const data = (result as { data?: { url?: string } })?.data ?? result
  return (data as { url?: string })?.url
}

export const useContactDriversLicenseAdmin = (
  contactId: string | undefined,
  storagePath: string | undefined,
) => {
  const queryClient = useQueryClient()
  const path = storagePath ?? ''
  const hasLicense = !!path

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invalidateContacts = () => {
    void queryClient.invalidateQueries({ queryKey: contactsQueryKey })
  }

  const loadPreviewUrl = useCallback(async () => {
    if (!contactId || !path) {
      setPreviewUrl(null)
      return
    }

    setIsLoadingUrl(true)
    setError(null)
    try {
      const result = await apiClient.portal.admin.getContactDriverLicenseFrontUrl(path, contactId)
      const url = extractUrl(result)
      setPreviewUrl(url ?? null)
    } catch (err) {
      console.error('Failed loading contact driver license preview:', err)
      setError(err instanceof Error ? err.message : 'Failed to load license photo')
      setPreviewUrl(null)
    } finally {
      setIsLoadingUrl(false)
    }
  }, [contactId, path])

  useEffect(() => {
    void loadPreviewUrl()
  }, [loadPreviewUrl])

  const uploadLicense = async (file: File) => {
    if (!contactId) {
      throw new Error('No contact selected')
    }

    setIsUploading(true)
    setError(null)
    try {
      const base64 = await fileToBase64(file)
      const result = await apiClient.portal.admin.uploadContactDriverLicenseFront(
        base64,
        contactId,
        getDriverLicenseUploadContentType(file),
      )
      const { url } = extractUploadResult(result)
      if (url) {
        setPreviewUrl(url)
      } else {
        await loadPreviewUrl()
      }
      invalidateContacts()
    } catch (err) {
      console.error('Failed uploading contact driver license:', err)
      setError(err instanceof Error ? err.message : 'Failed to upload license photo')
      throw err
    } finally {
      setIsUploading(false)
    }
  }

  const removeLicense = async () => {
    if (!contactId) {
      throw new Error('No contact selected')
    }

    setIsUploading(true)
    setError(null)
    try {
      await apiClient.portal.admin.removeContactDriverLicenseFront(contactId)
      setPreviewUrl(null)
      invalidateContacts()
    } catch (err) {
      console.error('Failed removing contact driver license:', err)
      setError(err instanceof Error ? err.message : 'Failed to remove license photo')
      throw err
    } finally {
      setIsUploading(false)
    }
  }

  return {
    hasLicense,
    previewUrl,
    isUploading,
    isLoadingUrl,
    error,
    uploadLicense,
    removeLicense,
    loadPreviewUrl,
  }
}
